import { BillScheduleCallbacks } from "./useBillScheduleCallbacks";

/**
 * What the bill grids need to show the Schedule column. Absent means the
 * column is off (flags, or a surface such as a task table that does not
 * host the schedule overlays).
 */
export interface BillScheduleWiring {
    callbacks: BillScheduleCallbacks;
    /** Set when arriving from a policy's "Add bills": floats it to the top and forces the column on. */
    prioritizedScheduleTemplateId?: string;
}

/** Marks a column the deep link added, so it can be told apart from a saved one. */
export const SCHEDULE_HANDOFF_COLUMN_OPTIONS = {
    pin: "right",
    customVariants: { variant: "schedule-handoff" },
} as const;

/**
 * Hides the Schedule column when schedules are off, and adds it after Amount
 * when a policy deep link needs it on screen and the saved view lacks it.
 */
export function resolveBillScheduleColumns<TColumn extends { id: string }>({
    columns,
    handoffColumn,
    amountColumnId,
    wiring,
}: {
    columns: TColumn[];
    handoffColumn: TColumn;
    amountColumnId: TColumn["id"];
    wiring: BillScheduleWiring | undefined;
}): TColumn[] {
    const isScheduleColumn = (column: TColumn) =>
        column.id === handoffColumn.id;
    if (!wiring) {
        return columns.filter((column) => !isScheduleColumn(column));
    }
    if (
        columns.some(isScheduleColumn) ||
        !wiring.prioritizedScheduleTemplateId
    ) {
        return columns;
    }
    const amountColumnIndex = columns.findIndex(
        (column) => column.id === amountColumnId,
    );
    const insertAt =
        amountColumnIndex < 0 ? columns.length : amountColumnIndex + 1;
    return [
        ...columns.slice(0, insertAt),
        handoffColumn,
        ...columns.slice(insertAt),
    ];
}
